import { useState, useEffect } from "react";

const zones = [
  { label: "UTC", tz: "UTC", color: "#6C63FF", emoji: "🌍" },
  { label: "India (IST)", tz: "Asia/Kolkata", color: "#FF6584", emoji: "🇮🇳" },
  { label: "PST", tz: "America/Los_Angeles", color: "#43E97B", emoji: "🌊" },
  { label: "EST", tz: "America/New_York", color: "#FA8231", emoji: "🗽" },
];

export default function App() {
  const [now, setNow] = useState(new Date());
  const [is24, setIs24] = useState(false);
  const [inputDate, setInputDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [inputHour, setInputHour] = useState("12");
  const [inputMin, setInputMin] = useState("00");
  const [inputAmPm, setInputAmPm] = useState("PM");
  const [fromZone, setFromZone] = useState("Asia/Kolkata");
  const [converted, setConverted] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const fmt = (tz, date) =>
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: !is24,
    }).format(date || now);

  const fmtDate = (tz, date) =>
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz, weekday: "long", day: "numeric", month: "long", year: "numeric",
    }).format(date || now);

  const fmtOffset = (tz) =>
    new Intl.DateTimeFormat("en", { timeZone: tz, timeZoneName: "shortOffset" })
      .formatToParts(now).find(p => p.type === "timeZoneName")?.value || "";

  const handleConvert = () => {
    if (!inputDate) return;
    let h = parseInt(inputHour);
    const m = parseInt(inputMin);
    if (!is24) {
      if (inputAmPm === "AM" && h === 12) h = 0;
      if (inputAmPm === "PM" && h !== 12) h += 12;
    }
    const [yr, mo, dy] = inputDate.split("-").map(Number);
    const utcGuess = new Date(Date.UTC(yr, mo - 1, dy, h, m, 0));
    const localParts = {};
    new Intl.DateTimeFormat("en-US", {
      timeZone: fromZone, hour: "2-digit", minute: "2-digit", hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit"
    }).formatToParts(utcGuess).forEach(({ type, value }) => localParts[type] = value);
    const diffH = h - parseInt(localParts.hour);
    const diffM = m - parseInt(localParts.minute);
    setConverted(new Date(utcGuess.getTime() + (diffH * 60 + diffM) * 60000));
  };

  const fmtConverted = (tz) =>
    converted ? new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: !is24,
    }).format(converted) : "--:--";

  const selectStyle = {
    padding: "12px 10px", borderRadius: 12, border: "2px solid rgba(255,255,255,0.2)",
    background: "#1a1a3e", color: "#fff", fontSize: 16, fontWeight: 700,
    outline: "none", cursor: "pointer",
  };

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(135deg,#0f0c29,#302b63,#24243e)", display:"flex", flexDirection:"column", alignItems:"center", padding:"40px 20px", fontFamily:"'Segoe UI',sans-serif" }}>
      <div style={{ textAlign:"center", marginBottom:28 }}>
        <h1 style={{ color:"#fff", fontSize:32, margin:0, letterSpacing:2, fontWeight:800 }}>🌐 World Clocks</h1>
        <p style={{ color:"rgba(255,255,255,0.5)", marginTop:6, fontSize:14 }}>Live timezone tracker & converter</p>
      </div>

      <button onClick={() => setIs24(p => !p)} style={{ marginBottom:28, padding:"10px 28px", borderRadius:50, border:"2px solid rgba(255,255,255,0.3)", background:is24?"rgba(255,255,255,0.15)":"transparent", color:"#fff", cursor:"pointer", fontSize:14, fontWeight:600 }}>
        {is24 ? "24H" : "12H"} Format
      </button>

      {/* Live Clocks */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:20, width:"100%", maxWidth:1200, marginBottom:36 }}>
        {zones.map(z => (
          <div key={z.tz} style={{ background:"rgba(255,255,255,0.07)", border:`2px solid ${z.color}55`, borderRadius:24, padding:"28px 24px", backdropFilter:"blur(12px)", boxShadow:`0 0 30px ${z.color}33`, position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:-40, right:-40, width:120, height:120, borderRadius:"50%", background:z.color, opacity:0.12, filter:"blur(20px)" }} />
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
              <span style={{ fontSize:24 }}>{z.emoji}</span>
              <div>
                <div style={{ color:z.color, fontWeight:800, fontSize:15 }}>{z.label}</div>
                <div style={{ color:"rgba(255,255,255,0.4)", fontSize:12 }}>{fmtOffset(z.tz)}</div>
              </div>
            </div>
            <div style={{ fontSize:34, fontWeight:900, color:"#fff", letterSpacing:2, fontVariantNumeric:"tabular-nums", lineHeight:1, marginBottom:12, textShadow:`0 0 20px ${z.color}88` }}>{fmt(z.tz)}</div>
            <div style={{ color:"rgba(255,255,255,0.55)", fontSize:12, borderTop:`1px solid ${z.color}33`, paddingTop:10 }}>📅 {fmtDate(z.tz)}</div>
          </div>
        ))}
      </div>

      {/* Converter */}
      <div style={{ width:"100%", maxWidth:1200, background:"rgba(255,255,255,0.07)", border:"2px solid rgba(255,255,255,0.15)", borderRadius:24, padding:"32px 28px", backdropFilter:"blur(12px)" }}>
        <h2 style={{ color:"#fff", margin:"0 0 8px", fontSize:20, fontWeight:800 }}>⏰ Time Converter</h2>
        <p style={{ color:"rgba(255,255,255,0.45)", marginBottom:20, fontSize:13 }}>Enter a specific date & time in any timezone and see it across all zones.</p>

        <div style={{ display:"flex", flexWrap:"wrap", gap:14, alignItems:"flex-end", marginBottom:28 }}>
          {/* Date */}
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ color:"rgba(255,255,255,0.6)", fontSize:12, fontWeight:600 }}>ENTER DATE</label>
            <input type="date" value={inputDate} onChange={e => setInputDate(e.target.value)}
              style={{ padding:"12px 16px", borderRadius:12, border:"2px solid rgba(255,255,255,0.2)", background:"rgba(255,255,255,0.1)", color:"#fff", fontSize:15, fontWeight:700, outline:"none", colorScheme:"dark" }} />
          </div>

          {/* Time */}
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ color:"rgba(255,255,255,0.6)", fontSize:12, fontWeight:600 }}>ENTER TIME</label>
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <select value={inputHour} onChange={e => setInputHour(e.target.value)} style={selectStyle}>
                {(is24
                  ? Array.from({length:24}, (_, i) => String(i).padStart(2, "0"))
                  : Array.from({length:12}, (_, i) => String(i + 1).padStart(2, "0"))
                ).map(h => <option key={h} value={h}>{h}</option>)}
              </select>
              <span style={{ color:"#fff", fontSize:20, fontWeight:800 }}>:</span>
              <select value={inputMin} onChange={e => setInputMin(e.target.value)} style={selectStyle}>
                {Array.from({length:60}, (_, i) => String(i).padStart(2, "0")).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              {!is24 && (
                <select value={inputAmPm} onChange={e => setInputAmPm(e.target.value)} style={selectStyle}>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              )}
            </div>
          </div>

          {/* From Zone */}
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ color:"rgba(255,255,255,0.6)", fontSize:12, fontWeight:600 }}>FROM TIMEZONE</label>
            <select value={fromZone} onChange={e => setFromZone(e.target.value)}
              style={{ padding:"12px 16px", borderRadius:12, border:"2px solid rgba(255,255,255,0.2)", background:"#1a1a3e", color:"#fff", fontSize:15, fontWeight:600, outline:"none" }}>
              {zones.map(z => <option key={z.tz} value={z.tz}>{z.emoji} {z.label}</option>)}
            </select>
          </div>

          <button onClick={handleConvert} style={{ padding:"12px 32px", borderRadius:12, border:"none", background:"linear-gradient(135deg,#6C63FF,#FF6584)", color:"#fff", fontSize:15, fontWeight:800, cursor:"pointer" }}>
            Convert →
          </button>
        </div>

        {/* Results */}
        {converted && (
          <div>
            <div style={{ color:"rgba(255,255,255,0.5)", fontSize:13, marginBottom:14 }}>
              🔄 Showing conversions for <span style={{ color:"#fff", fontWeight:700 }}>{fmtConverted(fromZone)}</span> {zones.find(z => z.tz === fromZone)?.label}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:14 }}>
              {zones.map(z => {
                const isSrc = z.tz === fromZone;
                return (
                  <div key={z.tz} style={{ background:isSrc?`${z.color}22`:"rgba(255,255,255,0.05)", border:`2px solid ${isSrc?z.color:z.color+"44"}`, borderRadius:16, padding:"18px 20px", boxShadow:isSrc?`0 0 20px ${z.color}44`:"none" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                      <span style={{ fontSize:18 }}>{z.emoji}</span>
                      <span style={{ color:z.color, fontWeight:700, fontSize:13 }}>{z.label}{isSrc ? " 📍" : ""}</span>
                    </div>
                    <div style={{ fontSize:28, fontWeight:900, color:"#fff", fontVariantNumeric:"tabular-nums", textShadow:`0 0 15px ${z.color}88` }}>{fmtConverted(z.tz)}</div>
                    <div style={{ color:"rgba(255,255,255,0.35)", fontSize:11, marginTop:6 }}>📅 {new Intl.DateTimeFormat("en-US",{timeZone:z.tz,day:"numeric",month:"short",year:"numeric"}).format(converted)}</div>
                    <div style={{ color:"rgba(255,255,255,0.25)", fontSize:11, marginTop:2 }}>{fmtOffset(z.tz)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <p style={{ color:"rgba(255,255,255,0.2)", marginTop:32, fontSize:12 }}>Updates every second · Time converter uses selected date</p>
    </div>
  );
}